// src/pages/Services.jsx - Complete Mobile Optimized
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, Cloud, Shield, Cpu, Brain, Database, CheckCircle, Blocks, Gamepad2, Zap, Rocket, Award, Users, Clock, TrendingUp } from 'lucide-react';

export default function ServicesOverview() {
  const services = [
    {
      icon: <Code className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "Software Development",
      description: "Custom software solutions tailored to meet your specific business requirements and goals",
      link: "/services/software-development",
      features: ["Web Applications", "Mobile Apps", "Desktop Software", "API Development"],
      color: "from-blue-500 to-cyan-500",
      category: "Development"
    },
    {
      icon: <Cloud className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "Cloud Computing",
      description: "Scalable cloud infrastructure, migration services, and cloud-native application development",
      link: "/services/cloud-computing",
      features: ["AWS Solutions", "Azure Services", "Google Cloud", "Cloud Migration"],
      color: "from-cyan-500 to-blue-500",
      category: "Infrastructure"
    },
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "Cyber Security",
      description: "Comprehensive security solutions to protect your business from digital threats",
      link: "/services/cyber-security",
      features: ["Penetration Testing", "Security Audits", "Compliance", "Threat Detection"],
      color: "from-red-500 to-orange-500",
      category: "Security"
    },
    {
      icon: <Cpu className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "VLSI Design",
      description: "Advanced VLSI design, verification, and semiconductor engineering services",
      link: "/services/vlsi-design",
      features: ["ASIC Design", "FPGA Development", "RTL Design", "Physical Design"],
      color: "from-purple-500 to-pink-500",
      category: "Hardware"
    },
    {
      icon: <Brain className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "AI & ML Solutions",
      description: "Intelligent solutions powered by artificial intelligence and machine learning",
      link: "/services/ai-ml-solutions",
      features: ["Machine Learning", "Deep Learning", "NLP", "Computer Vision"],
      color: "from-green-500 to-emerald-500",
      category: "AI & Data"
    },
    {
      icon: <Database className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "Data Analytics",
      description: "Transform your data into actionable insights with advanced analytics solutions",
      link: "/services/data-analytics",
      features: ["Business Intelligence", "Data Warehousing", "Predictive Analytics", "Data Visualization"],
      color: "from-indigo-500 to-purple-500",
      category: "AI & Data"
    },
    {
      icon: <Cpu className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "VLSI Services",
      description: "Comprehensive VLSI and semiconductor design services for cutting-edge chip development",
      link: "/services/vlsi",
      features: ["Chip Design", "Verification", "DFT", "Layout Design"],
      color: "from-violet-500 to-purple-500",
      category: "Hardware"
    },
    {
      icon: <Blocks className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "Blockchain & Web3",
      description: "Decentralized solutions, smart contracts, and blockchain development services",
      link: "/services/blockchain",
      features: ["Smart Contracts", "DApps", "NFT Development", "Web3 Integration"],
      color: "from-amber-500 to-orange-500",
      category: "Emerging Tech"
    },
    {
      icon: <Gamepad2 className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "Game Development",
      description: "Engaging and immersive game development across multiple platforms",
      link: "/services/game-development",
      features: ["Mobile Games", "PC Games", "VR/AR Games", "Game Design"],
      color: "from-pink-500 to-rose-500",
      category: "Development"
    },
    {
      icon: <Zap className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "AI Automation",
      description: "Intelligent automation solutions to streamline your business processes",
      link: "/services/ai-automation",
      features: ["Process Automation", "RPA", "AI Workflows", "Chatbots"],
      color: "from-yellow-500 to-orange-500",
      category: "AI & Data"
    },
    {
      icon: <Rocket className="w-6 h-6 sm:w-8 sm:h-8 lg:w-12 lg:h-12" />,
      title: "Startup MVP",
      description: "Rapid MVP development to validate your startup idea and secure funding",
      link: "/services/startup-mvp",
      features: ["Fast Prototyping", "Market Validation", "Scalable Architecture", "Investor Ready"],
      color: "from-teal-500 to-green-500",
      category: "Emerging Tech"
    }
  ];

  const stats = [
    { icon: <Award className="w-6 h-6 sm:w-8 sm:h-8" />, value: "50+", label: "Projects Delivered", color: "text-blue-600" },
    { icon: <Users className="w-6 h-6 sm:w-8 sm:h-8" />, value: "250+", label: "Happy Clients", color: "text-green-600" },
    { icon: <Clock className="w-6 h-6 sm:w-8 sm:h-8" />, value: "24/7", label: "Support Available", color: "text-purple-600" },
    { icon: <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8" />, value: "98%", label: "Success Rate", color: "text-orange-600" }
  ];

  const whyChooseUs = [
    "Expert team with 15+ years of combined experience",
    "Cutting-edge technology stack and tools",
    "Agile development methodology",
    "24/7 technical support and maintenance",
    "Competitive pricing with flexible models",
    "On-time delivery guarantee"
  ];

  const industries = [
    { name: "Healthcare", icon: "🏥" },
    { name: "Finance", icon: "💰" },
    { name: "E-commerce", icon: "🛒" },
    { name: "Education", icon: "📚" },
    { name: "Manufacturing", icon: "🏭" },
    { name: "Real Estate", icon: "🏢" }
  ];

  const technologies = [
    "React", "Node.js", "Python", "AWS", "Azure", "MongoDB", 
    "PostgreSQL", "Docker", "Kubernetes", "TensorFlow", "Flutter", "Unity"
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white overflow-hidden px-2 sm:px-4 lg:px-8">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 lg:mb-6 leading-tight">
                Our Services
              </h1>
              <p className="text-sm sm:text-base lg:text-xl text-blue-100 mb-4 sm:mb-6 lg:mb-8 leading-relaxed">
                Comprehensive technology solutions designed to drive innovation and accelerate your business growth
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:gap-4">
                <Link to="/contact" className="px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-xs sm:text-sm lg:text-base">
                  Get Started
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-xs sm:text-sm lg:text-base">
                  View Our Work
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 sm:py-12 lg:py-16 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center bg-white dark:bg-gray-900 p-3 sm:p-4 lg:p-6 rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition">
                <div className={`flex justify-center ${stat.color} mb-2 sm:mb-3 lg:mb-3`}>
                  {stat.icon}
                </div>
                <div className="text-xl sm:text-2xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">{stat.value}</div>
                <div className="text-xs sm:text-sm lg:text-base text-gray-600 dark:text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-16 lg:py-20 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 lg:mb-4">
              What We Offer
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              Choose from our wide range of specialized services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="group bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-4 sm:p-6 lg:p-8 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl sm:hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 sm:hover:-translate-y-2 relative overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                
                {/* Category Badge */}
                <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 sm:px-3 py-1 rounded text-xs">
                    {service.category}
                  </span>
                </div>
                
                <div className={`relative inline-flex p-2.5 sm:p-3 lg:p-4 bg-gradient-to-br ${service.color} rounded-lg sm:rounded-xl text-white mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {service.icon}
                </div>
                
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {service.title}
                </h3>
                
                <p className="text-xs sm:text-sm lg:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 leading-relaxed">
                  {service.description}
                </p>

                <ul className="space-y-1 sm:space-y-2 mb-4 sm:mb-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-blue-600 rounded-full mr-2 flex-shrink-0"></div>
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="flex items-center text-blue-600 dark:text-blue-400 font-semibold group-hover:translate-x-1 sm:group-hover:translate-x-2 transition-transform text-xs sm:text-sm lg:text-base">
                  <span>Learn More</span>
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 ml-1 sm:ml-2" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 lg:mb-4">
              Industries We Serve
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-gray-400">
              Delivering solutions across diverse sectors
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 lg:gap-6">
            {industries.map((industry, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 p-4 sm:p-5 lg:p-6 rounded-lg shadow-md text-center hover:scale-105 transition hover:shadow-lg">
                <div className="text-3xl sm:text-4xl lg:text-5xl mb-2 sm:mb-3">{industry.icon}</div>
                <h3 className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm lg:text-base">{industry.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-12 sm:py-16 lg:py-20 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3 lg:mb-4">
              Technologies We Master
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 lg:gap-4">
            {technologies.map((tech, index) => (
              <div key={index} className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 rounded-full font-semibold shadow-lg hover:scale-105 transition text-xs sm:text-sm lg:text-base">
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-5 lg:mb-6 leading-tight">
                Why Choose NEVXOAULIO VENTURES?
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 lg:mb-8 leading-relaxed">
                We combine technical expertise with business acumen to deliver solutions that not only work but drive real business value.
              </p>
              <ul className="space-y-2 sm:space-y-3 lg:space-y-4">
                {whyChooseUs.map((item, index) => (
                  <li key={index} className="flex items-start space-x-2 sm:space-x-3">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-green-600 flex-shrink-0 mt-0.5 sm:mt-1" />
                    <span className="text-xs sm:text-sm lg:text-base text-gray-700 dark:text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative mt-6 lg:mt-0">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                alt="Team collaboration"
                className="rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl w-full"
              />
              <div className="absolute -bottom-4 sm:-bottom-6 -right-4 sm:-right-6 bg-white dark:bg-gray-900 rounded-lg sm:rounded-xl shadow-lg sm:shadow-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-600 mb-1 sm:mb-2">50+</div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Projects Delivered</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-600 to-indigo-700 px-2 sm:px-4 lg:px-8">
        <div className="max-w-4xl mx-auto text-center text-white">
          <Rocket className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 mx-auto mb-4 sm:mb-5 lg:mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 lg:mb-6 leading-tight">
            Ready to Transform Your Business?
          </h2>
          <p className="text-sm sm:text-base lg:text-xl text-blue-100 mb-6 sm:mb-7 lg:mb-8 px-2 leading-relaxed">
            Let's discuss how our services can help you achieve your goals
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 lg:gap-6">
            <Link
              to="/contact"
              className="w-full sm:w-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2 text-xs sm:text-sm lg:text-base"
            >
              <span>Get Free Consultation</span>
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
            </Link>
            <a
              href="tel:+919449061503"
              className="w-full sm:w-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-xs sm:text-sm lg:text-base text-center"
            >
              Call: +91 94490 61503
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}